import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  Banknote,
  Briefcase,
  Flame,
  Gavel,
  LandPlot,
  Leaf,
  Search,
  SlidersHorizontal,
  Snowflake,
  Target,
  Thermometer,
  type LucideIcon,
} from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { Card, Chip, Input, Modal, Select, Stat } from '@/ui/primitives';
import { GridAuto } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { useBoard } from '../api';
import {
  ESTAGIO_LABEL,
  ESTAGIOS,
  FRENTES,
  FRENTE_LABEL,
  TEMPERATURAS,
  TEMPERATURA_LABEL,
  type Card as CardData,
  type Estagio,
  type Frente,
  type Temperatura,
} from '../schemas';

const FRENTE_ICON: Record<Frente, LucideIcon> = {
  ambiental: Leaf,
  credito: Banknote,
  fundiario: LandPlot,
  juridico: Gavel,
};

const FRENTE_TONE: Record<Frente, 'ok' | 'accent' | 'warn' | 'default'> = {
  ambiental: 'ok',
  credito: 'accent',
  fundiario: 'warn',
  juridico: 'default',
};

const TEMPERATURA_ICON: Record<Temperatura, LucideIcon> = {
  quente: Flame,
  morna: Thermometer,
  fria: Snowflake,
};

const TEMPERATURA_TONE: Record<Temperatura, 'danger' | 'warn' | 'default'> = {
  quente: 'danger',
  morna: 'warn',
  fria: 'default',
};

export function RelatoriosCardsView() {
  const navigate = useNavigate();
  const { data } = useBoard('');
  const total = data?.colunas.reduce((s, c) => s + c.processos.length, 0) ?? 0;

  return (
    <div className="pipe-grid !px-0">
      <button
        type="button"
        onClick={() =>
          void navigate({ to: '/crm/relatorios/oportunidades' })
        }
        className="pipe-sel"
      >
        <span className="pipe-sel-icon pipe-sel-icon--accent">
          <Target size={20} strokeWidth={1.5} />
        </span>
        <span className="pipe-sel-title">Oportunidades</span>
        <div className="flex gap-1 mt-1 flex-wrap">
          <Chip tone="info" size="sm">
            {total} {total === 1 ? 'registro' : 'registros'}
          </Chip>
        </div>
      </button>
    </div>
  );
}

function loteEGleba(denominacao: string | null | undefined): string {
  return denominacao && denominacao.trim() ? denominacao.trim() : '—';
}

function ServicoBarChart({
  data,
}: {
  data: Array<{ servico: string; qtd: number; cards: CardData[] }>;
}) {
  const [openServico, setOpenServico] = useState<string | null>(null);
  const max = Math.max(1, ...data.map((d) => d.qtd));
  const opened = data.find((d) => d.servico === openServico) ?? null;

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-center gap-2">
        <Briefcase size={16} strokeWidth={1.5} className="text-text-muted" />
        <span className="text-xs font-medium uppercase tracking-wider text-text-muted">
          Oportunidades por tipo de serviço
        </span>
        <span className="text-[0.7rem] text-text-muted ml-auto">
          clique na barra para ver os clientes
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {data.map(({ servico, qtd }) => {
          const pct = (qtd / max) * 100;
          return (
            <button
              key={servico}
              type="button"
              onClick={() => setOpenServico(servico)}
              className={cn(
                'flex items-center gap-3 w-full text-left rounded-md px-1 py-1 -mx-1',
                'hover:bg-surface2 transition-colors cursor-pointer',
              )}
            >
              <div
                className="text-[0.78rem] text-text-2 shrink-0 leading-tight"
                style={{ width: 260 }}
                title={servico}
              >
                {servico}
              </div>
              <div className="flex-1 h-5 rounded-md bg-surface2 relative overflow-hidden">
                <div
                  className="h-full rounded-md bg-accent transition-all"
                  style={{ width: `${pct}%`, opacity: 0.85 }}
                />
              </div>
              <div
                className="text-[0.82rem] font-semibold text-text shrink-0 tabular-nums"
                style={{ minWidth: 28, textAlign: 'right' }}
              >
                {qtd}
              </div>
            </button>
          );
        })}
      </div>

      <Modal
        open={!!opened}
        onOpenChange={(open) => {
          if (!open) setOpenServico(null);
        }}
        title={opened ? `Clientes — ${opened.servico}` : 'Clientes'}
        description={
          opened ? `${opened.qtd} ${opened.qtd === 1 ? 'oportunidade' : 'oportunidades'}` : ''
        }
        size="xl"
      >
        {opened && (
          <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
            {opened.cards.length === 0 ? (
              <span className="text-[0.82rem] text-text-muted italic">Nenhum cliente.</span>
            ) : (
              opened.cards.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center gap-3 text-[0.82rem] flex-wrap py-1.5 border-b border-border last:border-b-0"
                >
                  <span className="font-medium text-text">{c.cliente_nome ?? '—'}</span>
                  <span className="font-mono text-[0.76rem] text-text-2 whitespace-nowrap">
                    {c.cliente_cpf || '—'}
                  </span>
                  {c.imovel_denominacao && (
                    <span className="text-text-muted">· {c.imovel_denominacao}</span>
                  )}
                  <Chip tone="neutral" size="sm" className="ml-auto">
                    {ESTAGIO_LABEL[c.estagio] ?? c.estagio}
                  </Chip>
                </div>
              ))
            )}
          </div>
        )}
      </Modal>
    </Card>
  );
}

export function OportunidadesReport() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [openTemp, setOpenTemp] = useState<Temperatura | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [frente, setFrente] = useState<Frente | ''>('');
  const [etapa, setEtapa] = useState<Estagio | ''>('');
  const [servico, setServico] = useState('');
  const [regiao, setRegiao] = useState('');

  const { data, isLoading, error } = useBoard('');

  const allCards = useMemo<CardData[]>(() => {
    if (!data) return [];
    return data.colunas.flatMap((col) => col.processos);
  }, [data]);

  const servicos = useMemo(() => {
    const set = new Set<string>();
    for (const c of allCards) {
      if (c.servico && c.servico.trim()) set.add(c.servico.trim());
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [allCards]);

  const regioes = useMemo(() => {
    const set = new Set<string>();
    for (const c of allCards) {
      if (c.municipio && c.municipio.trim()) set.add(c.municipio.trim());
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [allCards]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allCards.filter((c) => {
      if (q) {
        const hay = [
          c.cliente_nome,
          c.cliente_cpf,
          c.imovel_denominacao,
          c.imovel_matricula,
          c.servico,
          c.municipio,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (frente && c.frente !== frente) return false;
      if (etapa && c.estagio !== etapa) return false;
      if (servico && (c.servico ?? '').trim() !== servico) return false;
      if (regiao && (c.municipio ?? '').trim() !== regiao) return false;
      return true;
    });
  }, [allCards, search, frente, etapa, servico, regiao]);

  const activeCount =
    (frente ? 1 : 0) + (etapa ? 1 : 0) + (servico ? 1 : 0) + (regiao ? 1 : 0);
  const filtroAtivo = activeCount > 0 || !!search.trim();

  const filterWrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!filtersOpen) return undefined;
    function onDown(e: MouseEvent) {
      if (filterWrapRef.current && !filterWrapRef.current.contains(e.target as Node)) {
        setFiltersOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setFiltersOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [filtersOpen]);

  function limparFiltros() {
    setSearch('');
    setFrente('');
    setEtapa('');
    setServico('');
    setRegiao('');
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => void navigate({ to: '/crm/relatorios' })}
          className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[0.78rem] border border-border bg-elevated text-text-2 hover:bg-surface2 hover:text-text transition-colors"
        >
          <ArrowLeft size={13} strokeWidth={1.5} />
          Voltar
        </button>

        <div className="relative flex-1 md:max-w-md">
          <Search
            size={14}
            strokeWidth={1.5}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
          />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, CPF, imóvel, serviço ou município"
            className="rounded-full pr-9"
          />
        </div>

        <div ref={filterWrapRef} className="relative">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 h-7 rounded-full text-[0.78rem]',
              'border border-border transition-colors',
              activeCount > 0 || filtersOpen
                ? 'bg-[rgba(217,119,87,0.1)] text-accent border-transparent'
                : 'bg-elevated text-text-2 hover:bg-surface2 hover:text-text',
            )}
          >
            <SlidersHorizontal size={13} strokeWidth={1.5} />
            Filtrar por
            {activeCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[15px] h-[15px] px-1 ml-1 rounded-full bg-accent text-[var(--on-solid)] text-[0.62rem] font-bold leading-none">
                {activeCount}
              </span>
            )}
          </button>

          {filtersOpen && (
            <div
              className={cn(
                'absolute top-[calc(100%+6px)] left-0 z-50',
                'bg-surface border border-border rounded-xl shadow-[var(--shadow-over)]',
                'p-4 min-w-[280px] flex flex-col gap-3',
              )}
            >
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="rel-flt-frente"
                  className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                >
                  Frente
                </label>
                <Select
                  id="rel-flt-frente"
                  value={frente}
                  onChange={(e) => setFrente(e.target.value as Frente | '')}
                  className="text-[0.82rem]"
                >
                  <option value="">Todas</option>
                  {FRENTES.map((f) => (
                    <option key={f} value={f}>
                      {FRENTE_LABEL[f]}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="rel-flt-etapa"
                  className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                >
                  Etapa
                </label>
                <Select
                  id="rel-flt-etapa"
                  value={etapa}
                  onChange={(e) => setEtapa(e.target.value as Estagio | '')}
                  className="text-[0.82rem]"
                >
                  <option value="">Todas</option>
                  {ESTAGIOS.map((e) => (
                    <option key={e} value={e}>
                      {ESTAGIO_LABEL[e]}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="rel-flt-servico"
                  className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                >
                  Tipo de serviço
                </label>
                <Select
                  id="rel-flt-servico"
                  value={servico}
                  onChange={(e) => setServico(e.target.value)}
                  className="text-[0.82rem]"
                  disabled={servicos.length === 0}
                >
                  <option value="">Todos</option>
                  {servicos.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="rel-flt-regiao"
                  className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                >
                  Região
                </label>
                <Select
                  id="rel-flt-regiao"
                  value={regiao}
                  onChange={(e) => setRegiao(e.target.value)}
                  className="text-[0.82rem]"
                  disabled={regioes.length === 0}
                >
                  <option value="">Todas</option>
                  {regioes.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Select>
              </div>

              <button
                type="button"
                onClick={limparFiltros}
                disabled={!filtroAtivo}
                className={cn(
                  'self-start text-[0.78rem] text-accent bg-transparent border-0 p-0',
                  'hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline',
                )}
              >
                Limpar filtros
              </button>
            </div>
          )}
        </div>

        <Chip tone="neutral" size="sm" className="ml-auto">
          {filtered.length} {filtered.length === 1 ? 'oportunidade' : 'oportunidades'}
        </Chip>
      </div>

      {isLoading && <div className="py-6 text-sm text-text-muted">carregando…</div>}
      {error && (
        <div className="py-6 text-sm text-danger">
          {error instanceof ApiError ? error.message : 'erro ao carregar oportunidades'}
        </div>
      )}

      {!isLoading && !error && (
        <GridAuto min="sm" gap="md">
          {FRENTES.map((f) => {
            const Icon = FRENTE_ICON[f];
            const qtd = allCards.filter((c) => c.frente === f).length;
            return (
              <Stat
                key={f}
                label={FRENTE_LABEL[f]}
                value={String(qtd)}
                tone={FRENTE_TONE[f]}
                icon={<Icon size={22} strokeWidth={1.5} />}
              />
            );
          })}
        </GridAuto>
      )}

      {!isLoading && !error && (
        <GridAuto min="sm" gap="md">
          {TEMPERATURAS.map((t) => {
            const Icon = TEMPERATURA_ICON[t];
            const cards = allCards.filter((c) => c.temperatura === t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => setOpenTemp(t)}
                className="text-left rounded-md transition-colors hover:bg-surface2/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
                aria-label={`Ver clientes ${TEMPERATURA_LABEL[t]}`}
              >
                <Stat
                  label={TEMPERATURA_LABEL[t]}
                  value={String(cards.length)}
                  tone={TEMPERATURA_TONE[t]}
                  icon={<Icon size={22} strokeWidth={1.5} />}
                />
              </button>
            );
          })}
        </GridAuto>
      )}

      <Modal
        open={!!openTemp}
        onOpenChange={(open) => {
          if (!open) setOpenTemp(null);
        }}
        title={
          openTemp
            ? `Clientes — Temperatura ${TEMPERATURA_LABEL[openTemp]}`
            : 'Clientes'
        }
        description={
          openTemp
            ? (() => {
                const n = allCards.filter((c) => c.temperatura === openTemp).length;
                return `${n} ${n === 1 ? 'oportunidade' : 'oportunidades'}`;
              })()
            : ''
        }
        size="xl"
      >
        {openTemp && (
          <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
            {(() => {
              const cards = allCards.filter((c) => c.temperatura === openTemp);
              if (cards.length === 0) {
                return (
                  <span className="text-[0.82rem] text-text-muted italic">
                    Nenhum cliente.
                  </span>
                );
              }
              return cards.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center gap-3 text-[0.82rem] flex-wrap py-1.5 border-b border-border last:border-b-0"
                >
                  <span className="font-medium text-text">{c.cliente_nome ?? '—'}</span>
                  <span className="font-mono text-[0.76rem] text-text-2 whitespace-nowrap">
                    {c.cliente_cpf || '—'}
                  </span>
                  {c.imovel_denominacao && (
                    <span className="text-text-muted">· {c.imovel_denominacao}</span>
                  )}
                  <Chip tone="neutral" size="sm" className="ml-auto">
                    {ESTAGIO_LABEL[c.estagio] ?? c.estagio}
                  </Chip>
                </div>
              ));
            })()}
          </div>
        )}
      </Modal>

      {!isLoading && !error && servicos.length > 0 && (
        <ServicoBarChart
          data={servicos
            .map((s) => {
              const cards = allCards.filter((c) => (c.servico ?? '').trim() === s);
              return { servico: s, qtd: cards.length, cards };
            })
            .sort((a, b) => b.qtd - a.qtd)}
        />
      )}

      {!isLoading && !error && (
        <div className="overflow-x-auto">
          <table className="table table-sm w-full">
            <thead>
              <tr>
                <th>Nome</th>
                <th>CPF</th>
                <th>Lote</th>
                <th>Imóvel</th>
                <th>Tipo do Serviço</th>
                <th>Etapa</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-text-muted py-6">
                    Nenhuma oportunidade encontrada.
                  </td>
                </tr>
              ) : (
                filtered.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontSize: '0.82rem' }}>{c.cliente_nome ?? '—'}</td>
                    <td className="whitespace-nowrap">
                      <span className="font-mono text-[0.78rem] text-text-2">
                        {c.cliente_cpf || '—'}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.82rem' }}>
                      {loteEGleba(c.imovel_denominacao)}
                    </td>
                    <td>
                      {c.imovel_matricula ? (
                        <Chip tone="info" size="sm">{c.imovel_matricula}</Chip>
                      ) : (
                        <span className="text-text-muted" style={{ fontSize: '0.78rem' }}>—</span>
                      )}
                    </td>
                    <td style={{ fontSize: '0.82rem' }}>{c.servico || '—'}</td>
                    <td>
                      <Chip tone="neutral" size="sm">
                        {ESTAGIO_LABEL[c.estagio] ?? c.estagio}
                      </Chip>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
